import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../../i18n/useP';
import { MOIS_LONG } from '../engine/constants';
import { startFromLastClosed } from '../engine/adapters';
import { formatSwissAmount } from '../billing/formatSwiss';
import '../StartingPointPage.css';

// Champ montant éditable (commit au blur).
function AmountInput({ value, onCommit, disabled }) {
  const [draft, setDraft] = useState(null);
  const display = draft !== null
    ? draft
    : (Number(value) ? formatSwissAmount(Number(value)) : '');
  return (
    <input
      type="text"
      inputMode="decimal"
      className="sp-amount"
      value={display}
      disabled={disabled}
      placeholder="0"
      onFocus={() => setDraft(String(Number(value) || ''))}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null) {
          const parsed = parseFloat(draft.replace(/[^0-9.-]/g, ''));
          const v = Number.isFinite(parsed) ? parsed : 0;
          if (v !== Number(value)) onCommit(v);
        }
        setDraft(null);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
}

// ── Section « Départ du plan » (mois/année + ancrage) ───────────────────────
// Sections exportées pour être composées dans le stepper « Point de départ »
// (StartingStepper). L'habillage card est porté par le stepper.
export function DepartDuPlanSection({ scenario, periodLabel, startMonth, yearOptions, lastClosed, locked, onPatch }) {
  const { t } = useTranslation();
  const p = useP();
  const anchor = startFromLastClosed(lastClosed);
  const anchored = anchor && startMonth === anchor.start_month && scenario?.start_year === anchor.start_year;
  const anchorLabel = anchor ? `${MOIS_LONG[anchor.start_month]} ${anchor.start_year}` : null;

  return (
    <>
      <div className="sp-start">
        <label className="sp-start__field">
          <span>{p('Mois de départ')}</span>
          <select value={startMonth} disabled={locked} onChange={(e) => onPatch({ start_month: Number(e.target.value) })}>
            {MOIS_LONG.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
        </label>
        <label className="sp-start__field">
          <span>{p('Année de départ')}</span>
          <select value={scenario?.start_year ?? yearOptions[1]} disabled={locked} onChange={(e) => onPatch({ start_year: Number(e.target.value) })}>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <span className="sp-start__note">{p("Bilan d'ouverture repris au")} <b>{periodLabel}</b></span>
      </div>
      {anchor && (
        <div className="sp-anchor">
          {anchored ? (
            <span className="sp-anchor__ok">{t('phrases:p_c6d0efcd16', { label: anchorLabel })}</span>
          ) : (
            <button
              type="button"
              className="sp-pull"
              disabled={locked}
              onClick={() => onPatch({ start_month: anchor.start_month, start_year: anchor.start_year })}
              title={t('phrases:p_e9a49f07ca')}
            >{t('phrases:p_b3e449bbf7', { label: anchorLabel })}</button>
          )}
        </div>
      )}
    </>
  );
}

// ── Section « Solde de trésorerie initial » ─────────────────────────────────
export function SoldeTresorerieSection({ resolved, scenario, periodLabel, locked, onPatch }) {
  const { t } = useTranslation();
  const p = useP();
  const financeTotal = resolved?.initialBalance;
  const liqAccts = resolved?.liquidityAccounts || [];
  const value = Number(scenario?.initial_balance) || 0;
  const fromBalance = financeTotal !== null && Math.abs(value - financeTotal) < 0.5;

  return (
    <>
      <div className="sp-soldes__row">
        <AmountInput value={value} disabled={locked} onCommit={(v) => onPatch({ initial_balance: v })} />
        <span className="sp-soldes__unit">CHF</span>
        <button
          type="button"
          className="sp-pull"
          disabled={locked || financeTotal === null}
          onClick={() => onPatch({ initial_balance: financeTotal })}
          title={t('phrases:p_76cf68845e')}
        >{financeTotal !== null
          ? t('phrases:p_f6c6927ddc', { amount: formatSwissAmount(financeTotal) })
          : p('↻ Tirer du bilan')}</button>
        <span className={`sp-soldes__src ${fromBalance ? 'is-bilan' : 'is-manual'}`}>
          {fromBalance ? p('lu du bilan') : p('saisi')}
        </span>
      </div>

      {liqAccts.length > 0 ? (
        <div className="sp-liq">
          <span className="sp-liq__title">{t('phrases:p_8f47c6a00c', { period: periodLabel })}</span>
          <table className="sp-liq__table">
            <tbody>
              {liqAccts.map((a) => (
                <tr key={a.account_number}>
                  <td><span className="sp-mono">{a.account_number}</span> {a.account_desc || '—'}</td>
                  <td className="sp-num">{formatSwissAmount(a.balance)}</td>
                </tr>
              ))}
              <tr className="sp-liq__total">
                <td>{p('Total liquidités')}</td>
                <td className="sp-num">{formatSwissAmount(financeTotal || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="sp-liq__empty">
          {t('phrases:p_fca7f4aaf1', { year: resolved?.period?.year })}
        </div>
      )}
    </>
  );
}
